"use client";

import QosmCode from "./QosmCode";

/**
 * Blog MDX component: a styled Qosm code panel.
 * Optionally split into "Provided by host" prelude and "Agent code" sections.
 *
 * Usage in MDX:
 *   <QosmPanel code={`let x = 1`} />
 *   <QosmPanel prelude={`extern foo : ...`} code={`foo {}`} filename="example.qosm" />
 */
export default function QosmPanel({
  prelude,
  code,
  filename = "example.qosm",
}: {
  prelude?: string;
  code: string;
  filename?: string;
}) {
  return (
    <div
      className="rounded-xl overflow-hidden border border-border my-6"
      style={{ background: "var(--color-bg-surface)" }}
    >
      {/* Title bar */}
      <div
        className="px-4 py-2 flex justify-between items-center border-b border-border"
        style={{ background: "var(--color-bg-elevated)" }}
      >
        <span className="font-mono text-xs" style={{ color: "var(--color-text-muted)" }}>
          {filename}
        </span>
        <span
          className="text-[0.6rem] font-semibold uppercase tracking-widest"
          style={{ color: "var(--color-text-muted)" }}
        >
          Qosm
        </span>
      </div>

      {/* Prelude — "Provided by host" */}
      {prelude && (
        <>
          <div className="relative" style={{ background: "rgba(255,255,255,0.02)" }}>
            <span
              className="absolute top-2 right-3 text-[9px] font-semibold uppercase tracking-widest"
              style={{ color: "var(--color-text-dim)" }}
            >
              Provided by host
            </span>
            <QosmCode
              code={prelude}
              className="m-0 px-5 pt-4 pb-3 text-[13px] leading-relaxed overflow-x-auto opacity-50"
            />
          </div>
          {/* Divider */}
          <div className="flex items-center gap-2 px-5" style={{ background: "var(--color-bg-elevated)" }}>
            <span className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
            <span
              className="text-[9px] font-semibold uppercase tracking-widest whitespace-nowrap"
              style={{ color: "var(--color-text-dim)" }}
            >
              Agent code
            </span>
            <span className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
          </div>
        </>
      )}

      {/* Main code */}
      <div style={{ background: "var(--color-bg-surface)" }}>
        <QosmCode
          code={code}
          className={`m-0 px-5 pb-4 text-[13px] leading-relaxed overflow-x-auto ${prelude ? "pt-2" : "pt-4"}`}
        />
      </div>
    </div>
  );
}